import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2DataGrid,
  V2FormGrid,
  V2FormField,
  V2FormActions,
  Icon,
  LoadingState,
  StatePanel,
  type V2TableColumn,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type {
  PurchaseRequest,
  PurchaseRequestListResponse,
  PurchaseRequestLineDraft,
  PurchaseRequestStatus,
} from '../../lib/types/procurement.js';
import { requestStatusOf } from '../../lib/types/procurement.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import type { ProjectListResponse } from '../../lib/types/projects.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';

const STATUS_TONE: Record<PurchaseRequestStatus, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'info',
  approved: 'brand',
  rejected: 'danger',
  cancelled: 'neutral',
};

const EMPTY_LINE: PurchaseRequestLineDraft = {
  productRef: '',
  description: '',
  quantity: '',
  uomRef: '',
  estimatedUnitPriceRial: '',
  notes: '',
};

export function RequestsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [statusFilter, setStatusFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [requestedDate, setRequestedDate] = useState('');
  const [neededByDate, setNeededByDate] = useState('');
  const [destinationWarehouseRef, setDestinationWarehouseRef] = useState('');
  const [projectRef, setProjectRef] = useState('');
  const [costCenterRef, setCostCenterRef] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<PurchaseRequestLineDraft[]>([{ ...EMPTY_LINE }]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const requests = useApiGet<PurchaseRequestListResponse>(
    slug
      ? `${base}/api/v1/procurement/requests${statusFilter ? `?status=${statusFilter}` : ''}`
      : null,
    [slug, statusFilter, refreshKey],
  );
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products` : null,
    [slug],
  );
  const uoms = useApiGet<MasterDataListResponse>(slug ? `${base}/api/v1/master-data/uoms` : null, [
    slug,
  ]);
  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?status=active` : null,
    [slug],
  );
  const projects = useApiGet<ProjectListResponse>(
    slug ? `${base}/api/v1/projects?limit=100` : null,
    [slug],
  );
  const costCenters = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/cost-centers?status=active` : null,
    [slug],
  );

  if (!slug) return null;

  function updateLine(index: number, patch: Partial<PurchaseRequestLineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  }
  function removeLine(index: number) {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }
  function resetForm() {
    setTitle('');
    setPurpose('');
    setRequestedDate('');
    setNeededByDate('');
    setDestinationWarehouseRef('');
    setProjectRef('');
    setCostCenterRef('');
    setNotes('');
    setLines([{ ...EMPTY_LINE }]);
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const validLines = lines.filter((l) => l.productRef && l.quantity && l.uomRef);
    if (!title.trim() || !requestedDate || validLines.length === 0) {
      setError('عنوان، تاریخ درخواست و حداقل یک قلم معتبر لازم است.');
      return;
    }
    setSubmitting(true);
    try {
      await apiPost(`${base}/api/v1/procurement/requests`, {
        title: title.trim(),
        purpose: purpose.trim() || undefined,
        requestedDate,
        neededByDate: neededByDate || undefined,
        destinationWarehouseRef: destinationWarehouseRef || undefined,
        projectRef: projectRef || undefined,
        costCenterRef: costCenterRef || undefined,
        notes: notes.trim() || undefined,
        lines: validLines.map((l) => ({
          productRef: l.productRef,
          description: l.description.trim() || undefined,
          quantity: l.quantity,
          uomRef: l.uomRef,
          estimatedUnitPriceRial: l.estimatedUnitPriceRial.trim() || undefined,
          notes: l.notes.trim() || undefined,
        })),
      });
      resetForm();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.envelope.error.message : 'ثبت درخواست ناموفق بود.');
    } finally {
      setSubmitting(false);
    }
  }

  const productOptions = products.status === 'ready' ? products.data.items : [];
  const uomOptions = uoms.status === 'ready' ? uoms.data.items : [];
  const productSelectState =
    products.status === 'loading'
      ? { placeholder: 'در حال بارگذاری کالاها…', disabled: true }
      : productOptions.length === 0
        ? { placeholder: 'کالایی در اطلاعات پایه ثبت نشده', disabled: true }
        : { placeholder: '— انتخاب کالا / خدمت —', disabled: false };
  const uomSelectState =
    uoms.status === 'loading'
      ? { placeholder: 'در حال بارگذاری واحدها…', disabled: true }
      : uomOptions.length === 0
        ? { placeholder: 'واحدی در اطلاعات پایه ثبت نشده', disabled: true }
        : { placeholder: '— انتخاب واحد —', disabled: false };

  const columns: readonly V2TableColumn<PurchaseRequest & { id: string }>[] = [
    {
      key: 'ref',
      header: 'شماره',
      render: (row) => (
        <Link
          to={`${base}/procurement/requests/${row.ref}`}
          className="ltr"
          style={{ fontFamily: 'var(--sk-font-mono)', fontSize: 13 }}
        >
          {row.ref}
        </Link>
      ),
    },
    { key: 'title', header: 'عنوان', render: (row) => row.title },
    {
      key: 'destinationWarehouse',
      header: 'انبار مقصد',
      render: (row) =>
        row.destinationWarehouseRef ? (
          <span>{row.destinationWarehouseName ?? row.destinationWarehouseRef}</span>
        ) : (
          <span className="sk-fg-muted">—</span>
        ),
    },
    {
      key: 'projectOrCostCenter',
      header: 'پروژه / مرکز هزینه',
      render: (row) => {
        const parts: string[] = [];
        if (row.projectRef) parts.push(`پروژه: ${row.projectName ?? row.projectRef}`);
        if (row.costCenterRef) parts.push(`مرکز: ${row.costCenterName ?? row.costCenterRef}`);
        return parts.length > 0 ? (
          <span style={{ fontSize: 13 }}>{parts.join(' | ')}</span>
        ) : (
          <span className="sk-fg-muted">—</span>
        );
      },
    },
    {
      key: 'requestedDate',
      header: 'تاریخ درخواست',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.requestedDate)}</span>,
    },
    {
      key: 'neededByDate',
      header: 'موعد نیاز',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.neededByDate)}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge tone={STATUS_TONE[requestStatusOf(row.statusLabel) ?? 'draft'] ?? 'neutral'}>
          {row.statusLabel}
        </V2Badge>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader title="درخواست خرید جدید" />
        <form
          onSubmit={onCreate}
          style={{ padding: '0 20px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <V2FormGrid>
            <V2FormField label="عنوان" required width="wide">
              <input
                className="sk-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="هدف / شرح نیاز" width="wide">
              <input
                className="sk-input"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="انبار مقصد" width="medium">
              <select
                className="sk-select"
                value={destinationWarehouseRef}
                onChange={(e) => setDestinationWarehouseRef(e.target.value)}
              >
                <option value="">— انبار مقصد را انتخاب کنید —</option>
                {warehouses.status === 'ready' &&
                  warehouses.data.items.map((w) => (
                    <option key={String(w['code'])} value={String(w['code'])}>
                      {String(w['name'] ?? w['code'])} ({String(w['code'])})
                    </option>
                  ))}
              </select>
            </V2FormField>
            <V2FormField label="پروژه" width="medium">
              <select
                className="sk-select"
                value={projectRef}
                onChange={(e) => setProjectRef(e.target.value)}
              >
                <option value="">— بدون پروژه (اختیاری) —</option>
                {projects.status === 'ready' &&
                  projects.data.projects.map((p) => (
                    <option key={p.ref} value={p.ref}>
                      {p.name} ({p.ref})
                    </option>
                  ))}
              </select>
            </V2FormField>
            <V2FormField label="مرکز هزینه" width="medium">
              <select
                className="sk-select"
                value={costCenterRef}
                onChange={(e) => setCostCenterRef(e.target.value)}
              >
                <option value="">— بدون مرکز هزینه (اختیاری) —</option>
                {costCenters.status === 'ready' &&
                  costCenters.data.items.map((c) => (
                    <option key={String(c['code'])} value={String(c['code'])}>
                      {String(c['name'] ?? c['code'])} ({String(c['code'])})
                    </option>
                  ))}
              </select>
            </V2FormField>
            <V2FormField label="تاریخ درخواست" required width="medium">
              <JalaliDateInput value={requestedDate} onChange={setRequestedDate} />
            </V2FormField>
            <V2FormField label="موعد نیاز" width="medium">
              <JalaliDateInput value={neededByDate} onChange={setNeededByDate} />
            </V2FormField>
          </V2FormGrid>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>اقلام درخواست</span>
            <V2Button type="button" variant="secondary" size="sm" icon="plus" onClick={addLine}>
              افزودن قلم
            </V2Button>
          </div>
          <div className="sk-table-scroll">
            <table className="sk-table">
              <thead>
                <tr>
                  <th style={{ width: 30 }}>#</th>
                  {/* `*` marks only the fields `onCreate`'s own `validLines`
                      filter (and the real `PurchaseRequestLineInput`
                      contract) actually require — productRef/quantity/
                      uomRef. Description/estimated price stay unmarked,
                      matching MovementForm.tsx's established convention. */}
                  <th style={{ minWidth: 160 }}>
                    کالا / خدمت <span style={{ color: 'var(--sk-danger)' }}>*</span>
                  </th>
                  <th style={{ minWidth: 140 }}>شرح</th>
                  <th style={{ width: 100 }}>
                    مقدار <span style={{ color: 'var(--sk-danger)' }}>*</span>
                  </th>
                  <th style={{ width: 110 }}>
                    واحد <span style={{ color: 'var(--sk-danger)' }}>*</span>
                  </th>
                  <th style={{ width: 140 }}>قیمت برآوردی (ریال)</th>
                  <th style={{ width: 44 }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => (
                  <tr key={i}>
                    <td className="sk-num">{i + 1}</td>
                    <td>
                      <select
                        className="sk-select"
                        style={{ width: '100%' }}
                        value={line.productRef}
                        disabled={productSelectState.disabled}
                        onChange={(e) => updateLine(i, { productRef: e.target.value })}
                      >
                        <option value="">{productSelectState.placeholder}</option>
                        {productOptions.map((p) => (
                          <option key={String(p.code)} value={String(p.code)}>
                            {String(p.name ?? p.code)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="sk-input"
                        style={{ width: '100%' }}
                        value={line.description}
                        onChange={(e) => updateLine(i, { description: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="sk-input ltr sk-num"
                        style={{ width: '100%' }}
                        value={line.quantity}
                        onChange={(e) => updateLine(i, { quantity: e.target.value })}
                      />
                    </td>
                    <td>
                      <select
                        className="sk-select"
                        style={{ width: '100%' }}
                        value={line.uomRef}
                        disabled={uomSelectState.disabled}
                        onChange={(e) => updateLine(i, { uomRef: e.target.value })}
                      >
                        <option value="">{uomSelectState.placeholder}</option>
                        {uomOptions.map((u) => (
                          <option key={String(u.code)} value={String(u.code)}>
                            {String(u.name ?? u.code)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="sk-input ltr sk-num"
                        style={{ width: '100%' }}
                        value={line.estimatedUnitPriceRial}
                        onChange={(e) => updateLine(i, { estimatedUnitPriceRial: e.target.value })}
                      />
                    </td>
                    <td>
                      {lines.length > 1 ? (
                        <button
                          type="button"
                          className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                          style={{ color: 'var(--sk-danger)' }}
                          onClick={() => removeLine(i)}
                        >
                          <Icon name="x" size="sm" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <V2FormField label="یادداشت" width="full">
            <textarea
              className="sk-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </V2FormField>

          <V2FormActions>
            {error ? (
              <span style={{ fontSize: 13, color: 'var(--sk-danger)', marginInlineEnd: 'auto' }}>
                {error}
              </span>
            ) : null}
            <V2Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'در حال ثبت…' : 'ثبت درخواست'}
            </V2Button>
          </V2FormActions>
        </form>
      </V2Card>

      {requests.status === 'loading' ? <LoadingState /> : null}
      {requests.status === 'error' ? (
        <StatePanel
          variant={requests.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={requests.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {requests.status === 'ready' ? (
        <V2DataGrid
          icon="cart"
          title="درخواست‌های خرید"
          subtitle={`${requests.data.requests.length.toLocaleString('fa-IR')} درخواست در دسترس شما`}
          filters={[
            {
              id: 'status',
              label: 'وضعیت',
              value: statusFilter,
              onChange: setStatusFilter,
              options: [
                { value: '', label: 'همه' },
                { value: 'draft', label: 'پیش‌نویس' },
                { value: 'submitted', label: 'ارسال‌شده' },
                { value: 'approved', label: 'تأیید‌شده' },
                { value: 'rejected', label: 'رد‌شده' },
                { value: 'cancelled', label: 'لغو‌شده' },
              ],
            },
          ]}
          columns={columns}
          rows={requests.data.requests.map((r) => ({ ...r, id: r.ref }))}
          rowKey={(r) => r.id}
          zebra
          emptyState={
            <V2EmptyState
              icon="cart"
              title="درخواست خریدی برای نمایش وجود ندارد."
              description="اولین درخواست خرید را ثبت کنید تا در صف بررسی قرار بگیرد."
            />
          }
        />
      ) : null}
    </div>
  );
}
